/* ==========================================================================
   AirSave™ theme – theme.js
   Vanilla JS, no dependencies. Powers: sticky header, ticker, menu drawer,
   search modal + predictive search, product gallery + zoom modal, bundle
   picker, Shopify AJAX cart + cart drawer, sticky add-to-cart bar, FAQ
   accordion, testimonials slider, reviews pagination + lightbox, section
   reveal animations, address form country/province selects.
   ========================================================================== */
(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const T = window.theme || { routes: {}, moneyFormat: '${{amount}}', reservedMinutes: 10, openDrawerOnAdd: true, strings: {} };

  /* Money formatting (Shopify money_format) */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || T.moneyFormat || '${{amount}}';
    const m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const type = m ? m[1] : 'amount';
    const num = (n, precision, thousands, decimal) => {
      thousands = thousands || ','; decimal = decimal || '.';
      if (isNaN(n) || n == null) return '0';
      n = (n / 100).toFixed(precision);
      const parts = n.split('.');
      const int = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return int + (parts[1] ? decimal + parts[1] : '');
    };
    let value = '';
    switch (type) {
      case 'amount': value = num(cents, 2); break;
      case 'amount_no_decimals': value = num(cents, 0); break;
      case 'amount_with_comma_separator': value = num(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = num(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = num(cents, 2, "'", '.'); break;
      default: value = num(cents, 2);
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* ------------------------------------------------------------------ */
  /* Sticky header                                                       */
  /* ------------------------------------------------------------------ */
  function initStickyHeader() {
    const section = $('.section-header');
    const wrapper = $('.header-wrapper');
    if (!section || !wrapper || section.dataset.init) return;
    section.dataset.init = '1';
    const type = wrapper.dataset.stickyType || 'on-scroll-up';
    const setVars = () => {
      document.documentElement.style.setProperty('--header-height', wrapper.offsetHeight + 'px');
      document.documentElement.style.setProperty('--header-bottom-position', section.getBoundingClientRect().bottom + 'px');
      document.documentElement.style.setProperty('--viewport-height', window.innerHeight + 'px');
    };
    setVars();
    on(window, 'resize', setVars);
    if (type === 'none') { section.style.position = 'relative'; return; }
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      const menuOpen = $('#Details-menu-drawer-container')?.open || $('.header__search details')?.open;
      if (type === 'always' || menuOpen) section.classList.remove('shopify-section-header-hidden');
      else if (y > lastY && y > section.offsetTop + wrapper.offsetHeight) section.classList.add('shopify-section-header-hidden');
      else if (y < lastY) section.classList.remove('shopify-section-header-hidden');
      lastY = y; setVars(); ticking = false;
    };
    on(window, 'scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
  }

  /* ------------------------------------------------------------------ */
  /* Menu drawer (mobile)                                                */
  /* ------------------------------------------------------------------ */
  function initMenuDrawer() {
    const details = $('#Details-menu-drawer-container');
    if (!details || details.dataset.init) return;
    details.dataset.init = '1';
    const summary = $('summary', details);
    const close = () => { details.open = false; summary.setAttribute('aria-expanded', 'false'); document.body.classList.remove('overflow-hidden-mobile'); $('.section-header')?.classList.remove('menu-open'); };
    on(summary, 'click', () => requestAnimationFrame(() => {
      summary.setAttribute('aria-expanded', String(details.open));
      $('.section-header')?.classList.toggle('menu-open', details.open);
      document.body.classList.toggle('overflow-hidden-mobile', details.open);
    }));
    on($('.menu-drawer__close-menu-btn', details), 'click', close);
    on(document, 'keyup', (e) => { if (e.key === 'Escape' && details.open) close(); });
    on(document, 'click', (e) => { if (details.open && !details.contains(e.target)) close(); });
    on(window, 'resize', () => { if (window.innerWidth >= 990 && details.open) close(); });
  }

  /* ------------------------------------------------------------------ */
  /* Search modal + predictive search (Section Rendering API)            */
  /* ------------------------------------------------------------------ */
  function initSearch() {
    const modal = $('.header__search');
    if (!modal || modal.dataset.init) return;
    modal.dataset.init = '1';
    const details = $('details', modal);
    const input = $('input[type="search"]', modal);
    const results = $('[data-predictive-search]', modal);
    const reset = $('.reset__button', modal);
    const summary = $('summary', details);
    const open = () => { details.open = true; setTimeout(() => input.focus(), 50); };
    const close = () => { details.open = false; if (results) results.hidden = true; input.value = ''; reset.classList.add('hidden'); summary.focus(); };
    on(summary, 'click', (e) => { e.preventDefault(); details.open ? close() : open(); });
    on($('.search-modal__close-button', modal), 'click', close);
    on($('.modal-overlay', modal), 'click', close);
    on(document, 'keyup', (e) => { if (e.key === 'Escape' && details.open) close(); });
    let timer, controller;
    on(input, 'input', () => {
      const q = input.value.trim();
      reset.classList.toggle('hidden', !q);
      clearTimeout(timer);
      if (!q) { if (results) results.hidden = true; return; }
      if (!T.routes.predictive_search_url || !results) return;
      timer = setTimeout(async () => {
        try {
          if (controller) controller.abort();
          controller = new AbortController();
          const url = `${T.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=4&section_id=predictive-search`;
          const res = await fetch(url, { signal: controller.signal });
          if (!res.ok) throw new Error(res.status);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('#shopify-section-predictive-search');
          results.innerHTML = inner ? inner.innerHTML : '';
          results.hidden = !results.innerHTML.trim();
          input.setAttribute('aria-expanded', 'true');
        } catch (e) { /* ignore aborted / offline */ }
      }, 250);
    });
    on(reset, 'click', () => setTimeout(() => { if (results) results.hidden = true; reset.classList.add('hidden'); input.focus(); }, 0));
  }

  /* ------------------------------------------------------------------ */
  /* Product gallery: slider, dots, thumbnails, arrows, zoom modal       */
  /* ------------------------------------------------------------------ */
  function initGallery(gallery) {
    if (gallery.dataset.init) return;
    gallery.dataset.init = '1';
    const list = $('[data-gallery]', gallery);
    if (!list) return;
    const slides = $$('.product__media-item', list);
    const dots = $$('.slider-counter__link', gallery);
    const thumbs = $$('.thumbnail', gallery);
    const strip = $('[data-thumbnails]', gallery);
    const prevBtn = $('.product-media-slider .slider-button--prev', gallery);
    const nextBtn = $('.product-media-slider .slider-button--next', gallery);
    const status = $('[role="status"]', gallery);
    let current = 0;

    const setActive = (idx) => {
      current = Math.max(0, Math.min(slides.length - 1, idx));
      slides.forEach((s, i) => s.classList.toggle('is-active', i === current));
      dots.forEach((d, i) => d.classList.toggle('slider-counter__link--active', i === current));
      thumbs.forEach((t, i) => { if (i === current) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
      if (prevBtn) prevBtn.disabled = current === 0;
      if (nextBtn) nextBtn.disabled = current === slides.length - 1;
      if (status) status.textContent = `Image ${current + 1} of ${slides.length}`;
      const li = thumbs[current] && thumbs[current].closest('li');
      if (li && strip) {
        const left = li.offsetLeft - strip.offsetLeft;
        if (left < strip.scrollLeft) strip.scrollTo({ left, behavior: 'smooth' });
        else if (left + li.offsetWidth > strip.scrollLeft + strip.clientWidth) strip.scrollTo({ left: left + li.offsetWidth - strip.clientWidth, behavior: 'smooth' });
      }
    };
    const goTo = (idx, smooth = true) => {
      const target = slides[Math.max(0, Math.min(slides.length - 1, idx))];
      if (!target) return;
      list.scrollTo({ left: target.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      setActive(idx);
    };
    thumbs.forEach((t, i) => on(t, 'click', () => goTo(i)));
    dots.forEach((d, i) => on(d, 'click', () => goTo(i)));
    on(prevBtn, 'click', () => goTo(current - 1));
    on(nextBtn, 'click', () => goTo(current + 1));
    on($('[data-thumb-prev]', gallery), 'click', () => strip && strip.scrollBy({ left: -strip.clientWidth, behavior: 'smooth' }));
    on($('[data-thumb-next]', gallery), 'click', () => strip && strip.scrollBy({ left: strip.clientWidth, behavior: 'smooth' }));
    let scrollTimer;
    on(list, 'scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => { const idx = Math.round(list.scrollLeft / list.clientWidth); if (idx !== current) setActive(idx); }, 80);
    }, { passive: true });
    on(window, 'resize', () => goTo(current, false));
    setActive(0);

    // Zoom modal (lives inside the same section)
    const section = gallery.closest('section');
    const modal = section && $('product-modal', section);
    if (modal) {
      const content = $('.product-media-modal__content', modal);
      const openModal = (mediaId) => {
        modal.setAttribute('open', ''); modal.setAttribute('aria-hidden', 'false'); document.body.classList.add('overflow-hidden');
        const target = mediaId ? $(`[data-media-id="${mediaId}"]`, content) : null;
        requestAnimationFrame(() => { if (target) target.scrollIntoView({ block: 'center' }); $('[data-modal-close]', modal).focus(); });
      };
      const closeModal = () => { modal.removeAttribute('open'); modal.setAttribute('aria-hidden', 'true'); document.body.classList.remove('overflow-hidden'); };
      $$('.product__media-toggle', list).forEach((btn) => on(btn, 'click', () => openModal(btn.dataset.mediaId)));
      on($('[data-modal-close]', modal), 'click', closeModal);
      on(modal, 'click', (e) => { if (e.target === modal || e.target === content || e.target.classList.contains('product-media-modal__dialog')) closeModal(); });
      on(document, 'keyup', (e) => { if (e.key === 'Escape' && modal.hasAttribute('open')) closeModal(); });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Shopify AJAX cart + drawer                                          */
  /* ------------------------------------------------------------------ */
  const Cart = {
    async request(url, body) {
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || T.strings.cartError || 'Cart error');
      return data;
    },
    add(items) { return Cart.request(T.routes.cart_add_url || '/cart/add', { items, sections: 'cart-drawer', sections_url: window.location.pathname }); },
    change(line, quantity) { return Cart.request(T.routes.cart_change_url || '/cart/change', { line, quantity, sections: 'cart-drawer', sections_url: window.location.pathname }); },
    async refresh() {
      const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
      const data = await res.json();
      return data['cart-drawer'];
    },
  };

  function replaceDrawer(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('cart-drawer');
    const current = $('cart-drawer');
    if (!fresh || !current) return;
    const wasActive = current.classList.contains('active');
    current.className = fresh.className + (wasActive ? ' active' : '');
    current.dataset.itemCount = fresh.dataset.itemCount;
    current.innerHTML = fresh.innerHTML;
    bindDrawerClose(current);
    updateBubble(Number(fresh.dataset.itemCount || 0));
    startCountdown();
  }

  function updateBubble(count) {
    $$('#cart-icon-bubble [data-cart-count]').forEach((el) => (el.textContent = String(count)));
    const bubble = $('.cart-count-bubble');
    if (bubble) bubble.hidden = count === 0;
  }

  function bindDrawerClose(drawer) {
    $$('[data-cart-close]', drawer).forEach((b) => on(b, 'click', closeDrawer));
    on($('#CartDrawer-Overlay', drawer), 'click', closeDrawer);
  }

  const openDrawer = () => {
    const drawer = $('cart-drawer'); if (!drawer) return;
    drawer.classList.add('active'); drawer.setAttribute('aria-hidden', 'false'); document.body.classList.add('overflow-hidden');
    setTimeout(() => $('.drawer__inner', drawer)?.focus(), 200);
  };
  const closeDrawer = () => {
    const drawer = $('cart-drawer'); if (!drawer) return;
    drawer.classList.remove('active'); drawer.setAttribute('aria-hidden', 'true'); document.body.classList.remove('overflow-hidden');
  };

  function showCartError(msg) {
    const el = $('#CartDrawer-CartErrors') || $('#CartErrors');
    if (el) { el.textContent = msg; setTimeout(() => (el.textContent = ''), 5000); } else alert(msg);
  }

  async function reloadCartPage() {
    const page = $('[data-cart-page]');
    if (!page) return false;
    const sectionEl = page.closest('.shopify-section');
    const id = sectionEl && sectionEl.id.replace('shopify-section-', '');
    if (!id) { window.location.reload(); return true; }
    try {
      const res = await fetch(`${window.location.pathname}?sections=${id}`);
      const data = await res.json();
      const doc = new DOMParser().parseFromString(data[id], 'text/html');
      const fresh = doc.querySelector('[data-cart-page]');
      if (fresh) page.replaceWith(fresh); else window.location.reload();
    } catch (e) { window.location.reload(); }
    return true;
  }

  function initCart() {
    if (document.body.dataset.cartInit) return;
    document.body.dataset.cartInit = '1';
    const drawer = $('cart-drawer');
    if (drawer) bindDrawerClose(drawer);
    on(document, 'keyup', (e) => { if (e.key === 'Escape' && $('cart-drawer')?.classList.contains('active')) closeDrawer(); });
    on($('#cart-icon-bubble'), 'click', (e) => { if (document.body.classList.contains('template-cart') || !drawer) return; e.preventDefault(); openDrawer(); });

    // quantity / remove (delegated: drawer + cart page)
    let busy = false;
    const change = async (line, qty) => {
      if (busy) return; busy = true;
      try {
        const data = await Cart.change(line, qty);
        replaceDrawer(data.sections && data.sections['cart-drawer']);
        await reloadCartPage();
      } catch (err) { showCartError(err.message); }
      busy = false;
    };
    on(document, 'click', (e) => {
      const t = e.target.closest('[data-remove],[data-qty-minus],[data-qty-plus]');
      if (!t) return;
      e.preventDefault();
      const item = t.closest('[data-line]');
      const line = Number(t.dataset.remove || t.dataset.qtyMinus || t.dataset.qtyPlus);
      const input = item && $('[data-qty-input]', item);
      const qty = input ? Number(input.value) : 1;
      if (t.dataset.remove) change(line, 0);
      else if (t.dataset.qtyMinus) change(line, Math.max(0, qty - 1));
      else change(line, qty + 1);
    });
    on(document, 'change', (e) => {
      const inp = e.target.closest('[data-qty-input]');
      if (inp) change(Number(inp.dataset.qtyInput), Math.max(0, parseInt(inp.value, 10) || 0));
    });

    // Add to cart (main + sticky buttons)
    on(document, 'click', async (e) => {
      const btn = e.target.closest('[data-add-to-cart]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      const form = btn.dataset.form ? document.getElementById(btn.dataset.form) : btn.closest('form');
      if (!form) return;
      const fd = new FormData(form);
      const id = Number(fd.get('id'));
      const quantity = Math.max(1, Number(fd.get('quantity')) || 1);
      const buttons = $$('[data-add-to-cart]');
      buttons.forEach((b) => { b.classList.add('loading'); b.setAttribute('aria-disabled', 'true'); });
      const errorWrap = $('.product-form__error-message-wrapper');
      if (errorWrap) errorWrap.hidden = true;
      try {
        const data = await Cart.add([{ id, quantity }]);
        replaceDrawer(data.sections && data.sections['cart-drawer']);
        if (T.openDrawerOnAdd !== false && $('cart-drawer')) openDrawer(); else window.location.href = T.routes.cart_url || '/cart';
      } catch (err) {
        if (errorWrap) { errorWrap.hidden = false; $('.product-form__error-message', errorWrap).textContent = err.message; } else showCartError(err.message);
      }
      buttons.forEach((b) => { b.classList.remove('loading'); b.removeAttribute('aria-disabled'); });
    });
    on(document, 'submit', (e) => { if (e.target.matches('[data-type="add-to-cart-form"]')) { e.preventDefault(); $('[data-add-to-cart]', e.target)?.click(); } });

    startCountdown();
  }

  /* Reserved-for countdown (per browser session) */
  let countdownTimer;
  function startCountdown() {
    const timer = $('countdown-timer');
    clearInterval(countdownTimer);
    if (!timer) return;
    const minutes = Number(timer.dataset.minutes) || T.reservedMinutes || 10;
    const drawer = $('cart-drawer');
    const key = 'airsave_cart_reserved_until';
    let until = 0;
    try { until = Number(sessionStorage.getItem(key)) || 0; } catch (e) { /* ignore */ }
    const hasItems = drawer && Number(drawer.dataset.itemCount || 0) > 0;
    if (hasItems && until < Date.now()) { until = Date.now() + minutes * 60000; try { sessionStorage.setItem(key, String(until)); } catch (e) { /* ignore */ } }
    const tick = () => {
      let left = hasItems ? Math.max(0, until - Date.now()) : minutes * 60000;
      const m = Math.floor(left / 60000), s = Math.floor((left % 60000) / 1000);
      timer.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };
    tick(); countdownTimer = setInterval(tick, 1000);
  }

  /* ------------------------------------------------------------------ */
  /* Sticky add-to-cart bar                                              */
  /* ------------------------------------------------------------------ */
  function initStickyAtc(bar) {
    if (bar.dataset.init || !('IntersectionObserver' in window)) return;
    bar.dataset.init = '1';
    const section = bar.closest('section');
    const anchor = section && $('.product-form__submit', section);
    if (!anchor) return;
    const toTop = $('#ScrollToTop');
    const io = new IntersectionObserver((entries) => {
      const visible = entries[0].isIntersecting;
      bar.dataset.afterScroll = visible ? 'true' : 'false';
      bar.setAttribute('aria-hidden', String(visible));
      toTop?.classList.toggle('is-above-sticky', !visible);
    }, { threshold: 0 });
    io.observe(anchor);
  }

  /* ------------------------------------------------------------------ */
  /* Misc                                                                */
  /* ------------------------------------------------------------------ */
  function initScrollTop() {
    const btn = $('#ScrollToTop');
    if (!btn || btn.dataset.init) return;
    btn.dataset.init = '1';
    const toggle = () => btn.classList.toggle('is-visible', window.scrollY > 400);
    on(window, 'scroll', toggle, { passive: true }); toggle();
    on(btn, 'click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  function initTicker(c) {
    if (c.dataset.init) return;
    c.dataset.init = '1';
    const items = $$('.horizontal-ticker__item', c);
    let guard = 0;
    while (c.scrollWidth < window.innerWidth * 2 && items.length && guard++ < 5) items.forEach((i) => c.appendChild(i.cloneNode(true)));
  }

  function initAccordions(root) {
    $$('.accordion__details', root).forEach((d) => {
      if (d.dataset.init) return;
      d.dataset.init = '1';
      on($('summary', d), 'click', (e) => {
        e.preventDefault();
        const willOpen = !d.open;
        const group = d.closest('.collapsible-content') || document;
        $$('.accordion__details', group).forEach((o) => { if (o !== d) o.open = false; });
        d.open = willOpen;
      });
    });
  }

  function initTestimonials(s) {
    if (s.dataset.init) return;
    s.dataset.init = '1';
    const list = $('.splide__list', s);
    const slides = $$('.splide__slide', s);
    const pag = $('.splide__pagination', s);
    if (!pag || slides.length < 2) return;
    slides.forEach((_, i) => {
      const li = document.createElement('li');
      li.innerHTML = `<button class="splide__pagination__page${i === 0 ? ' is-active' : ''}" type="button" role="tab" aria-label="Go to slide ${i + 1}"></button>`;
      pag.appendChild(li);
      on($('button', li), 'click', () => list.scrollTo({ left: slides[i].offsetLeft - 15, behavior: 'smooth' }));
    });
    const dots = $$('.splide__pagination__page', pag);
    let t;
    on(list, 'scroll', () => {
      clearTimeout(t);
      t = setTimeout(() => { const idx = Math.round(list.scrollLeft / (slides[0].offsetWidth + 15)); dots.forEach((d, i) => d.classList.toggle('is-active', i === idx)); }, 60);
    }, { passive: true });
  }

  function initReviews(widget) {
    if (widget.dataset.init) return;
    widget.dataset.init = '1';
    const perPage = Number(widget.dataset.perPage) || 5;
    const items = $$('.jm-review-item', widget);
    const nav = $('[data-review-pagination]', widget);
    let page = 1;
    const pages = Math.ceil(items.length / perPage);
    const show = (p, scroll) => {
      page = Math.max(1, Math.min(pages, p));
      items.forEach((it, i) => { it.hidden = !(i >= (page - 1) * perPage && i < page * perPage); });
      if (nav) $$('[data-page]', nav).forEach((b) => {
        const v = b.dataset.page;
        if (v === 'prev') b.disabled = page === 1;
        else if (v === 'next') b.disabled = page === pages;
        else { const active = Number(v) === page; b.classList.toggle('jm-button--primary', active); b.classList.toggle('jm-button--secondary', !active); if (active) b.setAttribute('aria-current', 'page'); else b.removeAttribute('aria-current'); }
      });
      if (scroll) widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (nav) on(nav, 'click', (e) => { const b = e.target.closest('[data-page]'); if (!b) return; const v = b.dataset.page; show(v === 'prev' ? page - 1 : v === 'next' ? page + 1 : Number(v), true); });
    show(1, false);
    on($('[data-write-review]', widget), 'click', () => alert('Install a reviews app (e.g. Judge.me) and add its app block to this section to collect reviews.'));
  }

  function initLightbox() {
    if ($('.jm-lightbox')) return;
    const lb = document.createElement('div');
    lb.className = 'jm-lightbox';
    lb.innerHTML = '<button type="button" class="jm-lightbox__close" aria-label="Close"><svg class="icon"><use href="#icon-close"/></svg></button><img alt="Customer review photo">';
    document.body.appendChild(lb);
    const lbImg = $('img', lb);
    const closeLb = () => lb.classList.remove('is-open');
    on(document, 'click', (e) => {
      const b = e.target.closest('[data-lightbox]');
      if (b) { lbImg.src = b.dataset.lightbox; lb.classList.add('is-open'); }
      else if (e.target === lb || e.target.closest('.jm-lightbox__close')) closeLb();
    });
    on(document, 'keyup', (e) => { if (e.key === 'Escape') closeLb(); });
  }

  function initAnimations(root) {
    const sections = $$('.animate-section', root);
    if (!sections.length) return;
    if (!('IntersectionObserver' in window) || window.Shopify?.designMode) { sections.forEach((s) => { s.classList.remove('animate--hidden'); s.classList.add('animate--shown'); }); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.remove('animate--hidden'); en.target.classList.add('animate--shown'); io.unobserve(en.target); } }), { rootMargin: '0px 0px -10% 0px' });
    sections.forEach((s) => io.observe(s));
  }

  function initForms(root) {
    on($('[data-track-form]', root), 'submit', (e) => {
      e.preventDefault(); const f = e.target;
      if (!f.checkValidity()) { f.reportValidity(); return; }
      $('[data-track-order]', f).textContent = $('#Track-number', f).value.trim();
      $('[data-track-result]', f).hidden = false;
    });
    on($('[data-recover-toggle]', root), 'click', (e) => { e.preventDefault(); $('#RecoverForm')?.scrollIntoView({ behavior: 'smooth' }); });
    // Country / province selects on address forms
    $$('select[name="address[country]"]', root).forEach((country) => {
      const province = document.getElementById(country.id.replace('Country', 'Province'));
      if (!province) return;
      const update = () => {
        const opt = country.options[country.selectedIndex];
        const provinces = JSON.parse(opt.getAttribute('data-provinces') || '[]');
        province.innerHTML = provinces.map((p) => `<option value="${p[0]}">${p[1]}</option>`).join('');
        province.closest('.field').style.display = provinces.length ? '' : 'none';
        if (province.dataset.default) province.value = province.dataset.default;
      };
      if (country.dataset.default) country.value = country.dataset.default;
      on(country, 'change', update); update();
    });
  }

  /* ------------------------------------------------------------------ */
  function init(root = document) {
    initStickyHeader();
    initMenuDrawer();
    initSearch();
    initCart();
    initScrollTop();
    initLightbox();
    $$('.horizontal-ticker__container', root).forEach(initTicker);
    $$('media-gallery', root).forEach(initGallery);
    $$('sticky-atc', root).forEach(initStickyAtc);
    $$('.testimonials-slider', root).forEach(initTestimonials);
    $$('[data-review-widget]', root).forEach(initReviews);
    initAccordions(root);
    initAnimations(root);
    initForms(root);
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const d = e.target.closest && e.target.closest('.accordion') && $('.accordion__details', e.target.closest('.accordion'));
    if (d) d.open = true;
  });

  window.AirSave = { formatMoney, openDrawer, closeDrawer, Cart };
})();
